'use strict';

document.addEventListener('DOMContentLoaded', () => {
    // --- DOM Element References ---
    const splitForm = document.getElementById('splitForm');
    const fileInput = document.querySelector('input[name="excelFile"]');
    const dropZone = document.getElementById('drop-zone');
    const fileNameDisplay = document.getElementById('fileNameDisplay');
    const fileError = document.getElementById('fileError');
    const generalError = document.getElementById('generalError');

    const sheetSelectionSection = document.getElementById('sheetSelectionSection');
    const sheetCountDisplay = document.getElementById('sheetCount');
    const selectAllSheetsBtn = document.getElementById('selectAllSheetsBtn');
    const deselectAllSheetsBtn = document.getElementById('deselectAllSheetsBtn');
    const sheetListUl = document.getElementById('sheetList');
    const sheetError = document.getElementById('sheetError');

    const splitButton = document.getElementById('splitButton');
    const progressSection = document.getElementById('progressSection');
    const percentageText = document.getElementById('percentage');
    const progressCircle = document.querySelector('.circular-progress .progress');
    const progressMessage = document.getElementById('progressMessage');
    const cancelButton = document.getElementById('cancelButton');
    const outputLog = document.getElementById('outputLog');
    const completionMessageElement = document.getElementById('completionMessage');

    // --- State Variables ---
    let uploadedFile = null;
    let currentTaskId = null;
    let eventSource = null;

    // --- Constants ---
    const PROGRESS_CIRCLE_CIRCUMFERENCE = 283;
    const DRAG_OVER_CLASS = 'drag-over';
    const INVALID_CLASS = 'invalid';

    // --- Utility Functions ---
    const clearErrorMessages = () => {
        if (fileError) fileError.textContent = '';
        if (sheetError) sheetError.textContent = '';
        if (generalError) generalError.textContent = '';
        if (dropZone) dropZone.classList.remove(INVALID_CLASS);
    };

    const clearLog = () => {
        if (outputLog) {
            outputLog.innerHTML = 'Status messages will appear here...';
        }
    };
    
    function logMessage(message, type = 'info', clearPreviousLog = false) {
        if(clearPreviousLog && outputLog) {
            clearLog();
        }
        if (outputLog) {
            const p = document.createElement('p');
            p.textContent = message;
            p.className = ''; 
            if (type === 'error') p.classList.add('error');
            else if (type === 'success') p.classList.add('success');
            else if (type === 'warning') p.classList.add('warning');
            else p.classList.add('info');

            const firstChild = outputLog.firstChild;
            if (firstChild && firstChild.nodeType === Node.TEXT_NODE && firstChild.textContent === 'Status messages will appear here...') {
                outputLog.innerHTML = '';
            }
            outputLog.insertBefore(p, outputLog.firstChild);
            outputLog.scrollTop = 0; 
        } else {
            const consoleMethod = type === 'error' ? 'error' : (type === 'warning' ? 'warn' : 'log');
            console[consoleMethod](message);
        }
    }

    const setTextContent = (element, text) => {
        if (element) element.textContent = text;
    };

    const toggleElementDisplay = (element, show) => {
        if (element) element.style.display = show ? 'block' : 'none';
    };

    const updateSplitButtonState = () => {
        if (!splitButton) return;
        const hasFile = uploadedFile !== null;
        const selectedSheets = sheetListUl.querySelectorAll('input[type="checkbox"]:checked');
        splitButton.disabled = !(hasFile && selectedSheets.length > 0);
    };

    const resetUIForNewFileSelection = () => {
        uploadedFile = null;
        if (fileInput) fileInput.value = ''; 
        setTextContent(fileNameDisplay, '');
        sheetListUl.innerHTML = '<li class="p-3 text-gray-500 italic">Upload an Excel file to see its sheets.</li>';
        setTextContent(sheetCountDisplay, '');
        
        toggleElementDisplay(sheetSelectionSection, false);
        toggleElementDisplay(completionMessageElement, false); 
        
        updateSplitButtonState();
        clearErrorMessages();
    };
    
    const resetProgressAndButtonsUI = (isError = true) => {
        toggleElementDisplay(progressSection, false);
        setTextContent(percentageText, '0%');
        if (progressCircle) progressCircle.style.strokeDashoffset = PROGRESS_CIRCLE_CIRCUMFERENCE;
        
        if (isError) {
            setTextContent(progressMessage, 'Processing failed or was cancelled.');
        } else {
            if (progressMessage && (progressMessage.textContent === 'Processing...' || progressMessage.textContent.startsWith('Sheet') || progressMessage.textContent.startsWith('Uploading file'))) {
                setTextContent(progressMessage, ''); 
            }
        }

        const progressBarContainer = document.getElementById('progressBarContainer');
        if (progressBarContainer) progressBarContainer.setAttribute('aria-valuenow', '0');
        
        updateSplitButtonState();
        toggleElementDisplay(cancelButton, false);
        toggleElementDisplay(completionMessageElement, false);
    };

    // --- File Handling & Sheet Listing ---
    const handleFileSelect = (file) => {
        resetUIForNewFileSelection(); 

        if (!file) {
            logMessage('No file selected or file input cleared.', 'info', true);
            return;
        }

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            setTextContent(fileError, 'Invalid file type. Please upload an .xlsx file.');
            if (dropZone) dropZone.classList.add(INVALID_CLASS);
            logMessage('Invalid file type. User attempted to upload: ' + file.name, 'warning');
            return;
        }

        uploadedFile = file;
        setTextContent(fileNameDisplay, `Selected file: ${file.name}`);
        if (dropZone) dropZone.classList.remove(INVALID_CLASS);
        setTextContent(fileError, '');

        processAndListSheets(file);
    };

    const processAndListSheets = (file) => {
        logMessage(`Processing "${file.name}" to list sheets...`, 'info', true);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target.result;
                if (typeof XLSX === 'undefined') {
                    throw new Error("XLSX library (SheetJS) is not loaded. Cannot read sheet names.");
                }
                const workbook = XLSX.read(data, { type: 'array', bookSheets: true });
                const localCurrentSheetNames = workbook.SheetNames || [];

                if (localCurrentSheetNames.length === 0) {
                    setTextContent(sheetError, 'The uploaded workbook contains no sheets.');
                    sheetListUl.innerHTML = '<li class="p-3 text-gray-500 italic">No sheets found in the workbook.</li>';
                    toggleElementDisplay(sheetSelectionSection, true);
                    logMessage('Uploaded workbook contains no sheets.', 'warning');
                } else {
                    populateSheetListUI(localCurrentSheetNames);
                    toggleElementDisplay(sheetSelectionSection, true);
                    logMessage(`Found ${localCurrentSheetNames.length} sheet(s). Please make selections.`, 'info');
                }
            } catch (error) {
                console.error("Error reading Excel file for sheet names:", error);
                logMessage(`Error reading file: ${error.message}. Ensure it's a valid .xlsx file.`, 'error');
                setTextContent(fileError, `Error reading file: ${error.message}. Please try another .xlsx file.`);
                if (dropZone) dropZone.classList.add(INVALID_CLASS);
                resetUIForNewFileSelection(); 
            }
            updateSplitButtonState();
        };
        reader.onerror = (err) => {
            console.error("FileReader error:", err);
            logMessage(`Failed to read the file: ${err.message}`, 'error');
            setTextContent(fileError, `Failed to read the file. It might be corrupted or unreadable.`);
            resetUIForNewFileSelection();
        };
        reader.readAsArrayBuffer(file);
    };

    const populateSheetListUI = (sheetNames) => {
        sheetListUl.innerHTML = '';
        setTextContent(sheetCountDisplay, `${sheetNames.length} sheet(s) found`);
        sheetNames.forEach((name, index) => {
            const li = document.createElement('li');
            const checkboxId = `sheet-${index}`;
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = checkboxId;
            checkbox.value = name;
            checkbox.name = "selectedSheetsToSplit";
            checkbox.className = "h-4 w-4 accent-emerald-600 border-gray-300 rounded focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 mr-2 cursor-pointer";
            checkbox.addEventListener('change', updateSplitButtonState);
            const label = document.createElement('label');
            label.htmlFor = checkboxId;
            label.textContent = name;
            label.className = "text-sm text-gray-700 cursor-pointer flex-grow";
            li.appendChild(checkbox);
            li.appendChild(label);
            sheetListUl.appendChild(li);
        });
    };

    // --- Event Handlers ---
    if (fileInput) {
        fileInput.addEventListener('change', (event) => {
            handleFileSelect(event.target.files[0]);
        });
    }

    if (dropZone) {
        dropZone.addEventListener('dragenter', (e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === 'file') dropZone.classList.add(DRAG_OVER_CLASS); });
        dropZone.addEventListener('dragover', (e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.items && e.dataTransfer.items.length > 0 && e.dataTransfer.items[0].kind === 'file') { e.dataTransfer.dropEffect = 'copy'; dropZone.classList.add(DRAG_OVER_CLASS); } else { e.dataTransfer.dropEffect = 'none'; }});
        dropZone.addEventListener('dragleave', (e) => { e.preventDefault(); e.stopPropagation(); if (e.target === dropZone || !dropZone.contains(e.relatedTarget)) dropZone.classList.remove(DRAG_OVER_CLASS);});
        dropZone.addEventListener('drop', (e) => {
            e.preventDefault(); e.stopPropagation();
            dropZone.classList.remove(DRAG_OVER_CLASS);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                if (fileInput) fileInput.files = e.dataTransfer.files;
                handleFileSelect(e.dataTransfer.files[0]);
            }
        });
    }

    if (selectAllSheetsBtn) {
        selectAllSheetsBtn.addEventListener('click', () => { sheetListUl.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = true); updateSplitButtonState(); });
    }
    if (deselectAllSheetsBtn) {
        deselectAllSheetsBtn.addEventListener('click', () => { sheetListUl.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false); updateSplitButtonState(); });
    }
    
    const handleCancellation = async () => {
        if (!currentTaskId) {
            logMessage('No active task to cancel.', 'info');
            resetProgressAndButtonsUI(true);
            return;
        }
        logMessage(`User initiated cancellation for task: ${currentTaskId}`, 'warning');
        if (eventSource) { eventSource.close(); eventSource = null; }
        setTextContent(generalError, 'Operation cancelled by user.');
        resetProgressAndButtonsUI(true); 

        try {
            const cancelResponse = await fetch(`/splitter/cancel-task/${currentTaskId}`, { method: 'POST' });
            if (cancelResponse.ok) {
                console.log(`[Client] Sent cancellation request for task ${currentTaskId} to server. Server acknowledged.`);
                logMessage('Cancellation request sent to server.', 'info');
            } else {
                console.warn(`[Client] Server responded with ${cancelResponse.status} to cancellation request for task ${currentTaskId}.`);
                logMessage('Server might not have processed cancellation.', 'warning');
            }
        } catch (err) { 
            console.error('Error sending cancellation request to server:', err);
            logMessage('Could not send cancellation to server. Please try again if process continues.', 'error');
        }
        currentTaskId = null;
    };

    if (cancelButton) {
        cancelButton.addEventListener('click', handleCancellation);
    }

    if (splitForm) {
        splitForm.addEventListener('submit', async (event) => {
            event.preventDefault();
            clearErrorMessages(); 
            setTextContent(generalError, '');
            toggleElementDisplay(completionMessageElement, false);

            if (!uploadedFile) {
                setTextContent(fileError, 'Please upload an Excel (.xlsx) file.');
                if (dropZone) dropZone.classList.add(INVALID_CLASS);
                return;
            }
            const selectedSheetsArray = Array.from(sheetListUl.querySelectorAll('input[type="checkbox"]:checked')).map(cb => cb.value);
            if (selectedSheetsArray.length === 0) {
                setTextContent(sheetError, 'Please select at least one sheet to split.');
                return;
            }

            splitButton.disabled = true;
            toggleElementDisplay(progressSection, true);
            toggleElementDisplay(cancelButton, true);
            setTextContent(percentageText, '0%');
            setTextContent(progressMessage, 'Initiating...');
            if (progressCircle) progressCircle.style.strokeDashoffset = PROGRESS_CIRCLE_CIRCUMFERENCE;
            const progressBarContainer = document.getElementById('progressBarContainer');
            if (progressBarContainer) progressBarContainer.setAttribute('aria-valuenow', '0');

            logMessage(`Starting split.`, 'info', true);

            try {
                const taskResponse = await fetch('/start-task');
                if (!taskResponse.ok) {
                    const errData = await taskResponse.json().catch(() => ({}));
                    throw new Error(errData.message || `Failed to initiate task (${taskResponse.status} ${taskResponse.statusText})`);
                }
                const taskData = await taskResponse.json();
                currentTaskId = taskData.taskId;
                if (!currentTaskId) throw new Error('Server did not provide a task ID.');
                logMessage(`Task started with ID: ${currentTaskId}`, 'info');

                eventSource = new EventSource(`/progress/${currentTaskId}`);
                eventSource.onopen = () => {
                    console.log(`[SSE] Connection opened for task ${currentTaskId}`);
                    logMessage(`[SSE] Connection opened for task ${currentTaskId}`, 'info');
                };

                eventSource.addEventListener('progress', (e) => {
                    try {
                        const data = JSON.parse(e.data);
                        console.log('[SSE] Progress Data:', data);
                        const percent = parseInt(data.progress, 10) || 0;
                        setTextContent(percentageText, `${percent}%`);
                        if (progressCircle) {
                            progressCircle.style.strokeDashoffset = PROGRESS_CIRCLE_CIRCUMFERENCE - (percent / 100) * PROGRESS_CIRCLE_CIRCUMFERENCE;
                        }
                        if (progressBarContainer) progressBarContainer.setAttribute('aria-valuenow', percent.toString());
                        if (data.message) setTextContent(progressMessage, data.message);
                    } catch (err) {
                        console.error('[SSE] Error parsing progress event data:', err, e.data);
                    }
                });

                eventSource.addEventListener('complete', (e) => {
                    try {
                        const data = JSON.parse(e.data);
                        console.log('[SSE] Complete Data:', data);
                        setTextContent(percentageText, '100%');
                        if (progressCircle) progressCircle.style.strokeDashoffset = 0;
                        if (progressBarContainer) progressBarContainer.setAttribute('aria-valuenow', '100');
                        setTextContent(progressMessage, data.message || 'Server processing complete. Preparing download...');
                        logMessage(`[SSE Complete] ${data.message || 'Server has finished processing.'}`, 'success');
                        if (eventSource) { eventSource.close(); eventSource = null; }
                        toggleElementDisplay(cancelButton, false);
                    } catch (err) {
                        console.error('[SSE] Error parsing complete event data:', err, e.data);
                        if (eventSource) { eventSource.close(); eventSource = null; }
                    }
                });
                
                eventSource.addEventListener('cancelled', (e) => {
                    try {
                        const data = JSON.parse(e.data);
                        console.log('[SSE] Cancelled Event Data:', data);
                        logMessage(`[SSE Cancelled Event] ${data.message || 'Task was cancelled on the server.'}`, 'warning');
                        setTextContent(generalError, data.message || 'Operation cancelled by server.');
                        resetProgressAndButtonsUI(true); 
                        if (eventSource) { eventSource.close(); eventSource = null; }
                        currentTaskId = null;
                    } catch (err) {
                        console.error('[SSE] Error parsing cancelled event data:', err, e.data);
                        setTextContent(generalError, 'Cancellation confirmed by server, but issue parsing message.');
                        resetProgressAndButtonsUI(true);
                        if (eventSource) { eventSource.close(); eventSource = null; }
                        currentTaskId = null;
                    }
                });

                eventSource.addEventListener('error_event', (e) => {
                    try {
                        const data = JSON.parse(e.data);
                        console.error('[SSE] Server Error Event Data:', data);
                        setTextContent(generalError, `Error: ${data.error || 'An unknown server error occurred during processing.'}`);
                        logMessage(`[SSE Server Error] ${data.error || 'Unknown server error.'}`, 'error');
                        setTextContent(progressMessage, 'Processing failed due to a server error.');
                    } catch (err) {
                        console.error('[SSE] Error parsing error_event data:', err, e.data);
                        setTextContent(generalError, 'Processing failed. Invalid error data received from server.');
                    } finally {
                        if (eventSource) { eventSource.close(); eventSource = null; }
                        resetProgressAndButtonsUI(true);
                        currentTaskId = null;
                    }
                });

                eventSource.onerror = (errEvent) => {
                    console.error('[SSE] Connection Error:', errEvent);
                    if (currentTaskId) {
                        if (!generalError.textContent) {
                             setTextContent(generalError, 'Connection to server lost. Please check your network and try again.');
                        }
                        logMessage('[SSE Connection Error] Connection to server was lost or server became unavailable.', 'error');
                        resetProgressAndButtonsUI(true); 
                    }
                    if (eventSource) { eventSource.close(); eventSource = null; }
                };

                const formData = new FormData();
                formData.append('excelFile', uploadedFile);
                formData.append('selectedSheets', JSON.stringify(selectedSheetsArray));
                formData.append('taskId', currentTaskId);

                setTextContent(progressMessage, 'Uploading file and configuration to server...');
                logMessage('Sending data to server for splitting...', 'info');

                const splitResponse = await fetch('/splitter/split-sheets', { method: 'POST', body: formData });

                if (!splitResponse.ok) {
                    const errData = await splitResponse.json().catch(() => ({ error: `Server responded with ${splitResponse.status}` }));
                    throw new Error(errData.error || `File processing failed on the server (${splitResponse.status})`);
                }
                const blob = await splitResponse.blob();
                if (blob.type === 'application/json' || !blob || blob.size === 0) {
                    const errorText = await blob.text();
                    let errorJson = {};
                    try { errorJson = JSON.parse(errorText); } catch (parseErr) { /* ignore */ }
                    console.error('Received JSON error or empty blob instead of ZIP:', errorJson.error || errorText);
                    throw new Error(errorJson.error || 'Server returned an invalid file. Expected a ZIP.');
                }

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none'; a.href = url;
                const disposition = splitResponse.headers.get('content-disposition');
                let downloadFilename = 'split_excel_sheets.zip';
                if (disposition && disposition.includes('attachment')) {
                    const filenameMatch = disposition.match(/filename\*?=['"]?([^'";\n]+)['"]?/i);
                    if (filenameMatch && filenameMatch[1]) {
                        downloadFilename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
                    }
                }
                a.download = downloadFilename;
                document.body.appendChild(a); a.click();
                window.URL.revokeObjectURL(url); a.remove();

                logMessage(`File "${downloadFilename}" download initiated.`, 'success');

                toggleElementDisplay(progressSection, false);
                const splitButtonContainer = document.getElementById('splitButtonContainer');
                if (splitButtonContainer) toggleElementDisplay(splitButtonContainer, false);
                if (completionMessageElement) toggleElementDisplay(completionMessageElement, true);

                currentTaskId = null;
                toggleElementDisplay(cancelButton, false);

                setTimeout(() => {
                    if (completionMessageElement) toggleElementDisplay(completionMessageElement, false);
                    if (splitButtonContainer) toggleElementDisplay(splitButtonContainer, true);
                    updateSplitButtonState();
                    logMessage('Ready to split again with current settings or upload a new file.', 'info');
                    
                    setTextContent(percentageText, '0%');
                    if (progressCircle) progressCircle.style.strokeDashoffset = PROGRESS_CIRCLE_CIRCUMFERENCE;
                    if (progressBarContainer) progressBarContainer.setAttribute('aria-valuenow', '0');
                    setTextContent(progressMessage, '');
                }, 5000);

            } catch (error) {
                console.error('Splitting process error:', error);
                logMessage(`Error: ${error.message}`, 'error');
                setTextContent(generalError, `Error: ${error.message}`);
                if (eventSource) { eventSource.close(); eventSource = null; }
                resetProgressAndButtonsUI(true);
                currentTaskId = null;
            } finally {
                if (eventSource && eventSource.readyState !== EventSource.CLOSED) {
                    eventSource.close();
                    eventSource = null;
                }
            }
        });
    }
    
    resetUIForNewFileSelection(); 
    if (outputLog) clearLog(); 
    logMessage('Please upload an Excel file to begin.', 'info');
});
